import React, { useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Button,
  Dialog,
  DialogClose,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Field,
  FieldContent,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSeparator,
  FieldSet,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupText,
  InputGroupTextarea,
  Spinner,
} from '@/shared/ui';
import { Mail, User, Building2, Send, Briefcase, Download } from 'lucide-react';
import { useSiteContent } from '@personal-portfolio/queries';
import { useFirebase } from '@/services/analytics/use-firebase';
import { logError } from '@personal-portfolio/common-utils';
import { cn } from '@/shared/lib/cn';
import { getUserId } from '@/services/analytics/user-id';

// A product/UI limit, not a storage one: `saveResumeRequest` writes `message`
// straight into a Firestore document field, which tolerates ~1 MB. This schema
// is therefore the only thing enforcing the bound, and the same constant drives
// the character counter shown under the textarea — keep them a single source.
const MAX_MESSAGE_LENGTH = 500;
const resumeFormSchema = z.object({
  name: z
    .string()
    .min(1, 'Name is required')
    .min(2, 'Name must be at least 2 characters long')
    .max(100, 'Name must be less than 100 characters'),
  email: z
    .email({
      message: 'Please enter a valid email address',
    })
    .max(254, 'Email address is too long'),
  company: z
    .string()
    .max(100, 'Company name must be less than 100 characters')
    .optional()
    .or(z.literal('')),
  position: z
    .string()
    .max(100, 'Position must be less than 100 characters')
    .optional()
    .or(z.literal('')),
  message: z
    .string()
    .max(
      MAX_MESSAGE_LENGTH,
      `Message must be less than ${MAX_MESSAGE_LENGTH} characters`,
    )
    .optional()
    .or(z.literal('')),
});

type ResumeFormData = z.infer<typeof resumeFormSchema>;

type SubmitStatus = 'idle' | 'success' | 'error';

const SUCCESS_AUTO_CLOSE_MS = 1500;

export const ResumeDownloadPopup: React.FC = () => {
  // Fully controlled dialog: always starts closed and every open/close goes
  // through setIsOpen, so the dialog keeps working after a submit.
  const [isOpen, setIsOpen] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<SubmitStatus>('idle');
  const { resume } = useSiteContent();
  const { trackEvent, saveResumeRequest } = useFirebase();

  const {
    register,
    handleSubmit: handleFormSubmit,
    formState: { errors, isSubmitting },
    control,
    reset,
    getValues,
  } = useForm<ResumeFormData>({
    resolver: zodResolver(resumeFormSchema),
    defaultValues: {
      name: '',
      email: '',
      company: '',
      position: '',
      message: '',
    },
    mode: 'onChange',
  });

  const message = useWatch({ control, name: 'message' });

  const onFormSubmit = async (data: ResumeFormData) => {
    try {
      // Save to Firestore first - only include fields with actual values
      const firestoreData: {
        userId: string;
        name: string;
        email: string;
        sendMethod: 'email';
        userAgent: string;
        currentPage: string;
        company?: string;
        position?: string;
        message?: string;
        referrer?: string;
      } = {
        // Synchronous read: the ID is generated/cached in localStorage at app
        // idle time (see AnalyticsService.initialize), never on the submit path.
        userId: getUserId(),
        name: data.name,
        email: data.email,
        sendMethod: 'email',
        userAgent: navigator.userAgent,
        currentPage: window.location.href,
        company: data.company?.trim() || undefined,
        position: data.position?.trim() || undefined,
        message: data.message?.trim() || undefined,
        referrer: document.referrer || undefined,
      };

      const requestId = await saveResumeRequest(firestoreData);

      // Track single completion event.
      // PII-free by design: only the request id, boolean has_* flags and page
      // context — never the visitor's name, email, or email domain.
      await trackEvent('resume_request_completed', {
        request_id: requestId,
        send_method: 'email',
        has_company: !!data.company,
        has_position: !!data.position,
        has_message: !!data.message,
        form_page: window.location.pathname,
      });

      // Reset the form and show the inline success message; the dialog
      // auto-closes shortly after.
      reset();
      setSubmitStatus('success');
    } catch (error) {
      logError('Error submitting resume request:', error);

      await trackEvent('resume_request_failed', {
        error: error instanceof Error ? error.message : 'Unknown error',
        send_method: 'email',
        form_page: window.location.pathname,
      });

      setSubmitStatus('error');
    }
  };

  // Auto-close after a successful submit.
  useEffect(() => {
    if (submitStatus !== 'success' || !isOpen) return;
    const timer = setTimeout(() => {
      setIsOpen(false);
      setSubmitStatus('idle');
    }, SUCCESS_AUTO_CLOSE_MS);
    return () => clearTimeout(timer);
  }, [submitStatus, isOpen]);

  const handleOpenChange = (open: boolean) => {
    // Always mirror the requested state so the dialog stays controlled.
    setIsOpen(open);

    if (open) {
      setSubmitStatus('idle');
      void trackEvent('resume_popup_opened', {
        form_page: window.location.pathname,
      });
      return;
    }

    // Read before reset(): `had_interaction` distinguishes an abandoned form
    // from an idle open, and the values are gone once the form is cleared.
    const formValues = getValues();
    void trackEvent('resume_popup_closed', {
      had_interaction: Object.values(formValues).some(
        (value) => typeof value === 'string' && value.trim() !== '',
      ),
      form_page: window.location.pathname,
    });

    reset();
    setSubmitStatus('idle');
  };

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={handleOpenChange}>
      <Button className="w-full">
        <Download className="mr-2 h-4 w-4" />
        {resume.trigger}
      </Button>
      {/* Viewport containment (max-h + overflow + edge gap) comes from the
          Dialog primitive; this popup only adds its own choice of
          full-screen presentation on phones. */}
      <Dialog className="custom-scrollbar max-sm:h-full max-sm:max-h-none max-sm:w-full max-sm:max-w-none max-sm:rounded-none max-sm:border-0 sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{resume.dialogTitle}</DialogTitle>
          <DialogDescription>{resume.dialogBody}</DialogDescription>
        </DialogHeader>
        <div aria-live="polite" role="status">
          {submitStatus === 'success' && (
            <p className="rounded-md border border-border bg-muted px-4 py-3 text-sm text-foreground">
              {resume.successMessage}
            </p>
          )}
          {submitStatus === 'error' && (
            <p className="rounded-md border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
              {resume.errorMessage}
            </p>
          )}
        </div>
        <form onSubmit={handleFormSubmit(onFormSubmit)} className="space-y-6">
          <FieldGroup>
            <FieldSet>
              <FieldLegend>{resume.form.personalLegend}</FieldLegend>
              <FieldGroup>
                <Field data-invalid={!!errors.name}>
                  <FieldLabel htmlFor="name">
                    {resume.form.nameLabel}
                  </FieldLabel>
                  <FieldContent>
                    <InputGroup>
                      <InputGroupInput
                        id="name"
                        {...register('name')}
                        placeholder={resume.form.namePlaceholder}
                        aria-invalid={!!errors.name}
                      />
                      <InputGroupAddon className="text-foreground">
                        <User className="stroke-2" />
                      </InputGroupAddon>
                    </InputGroup>
                    <FieldError>{errors.name?.message}</FieldError>
                  </FieldContent>
                </Field>

                <Field data-invalid={!!errors.email}>
                  <FieldLabel htmlFor="email">
                    {resume.form.emailLabel}
                  </FieldLabel>
                  <FieldContent>
                    <InputGroup>
                      <InputGroupInput
                        id="email"
                        type="email"
                        {...register('email')}
                        placeholder={resume.form.emailPlaceholder}
                        autoComplete="email"
                        aria-invalid={!!errors.email}
                      />
                      <InputGroupAddon className="text-foreground">
                        <Mail className="stroke-2" />
                      </InputGroupAddon>
                    </InputGroup>
                    <FieldError>{errors.email?.message}</FieldError>
                  </FieldContent>
                </Field>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Field data-invalid={!!errors.company}>
                    <FieldLabel htmlFor="company">
                      {resume.form.companyLabel}
                    </FieldLabel>
                    <FieldContent>
                      <InputGroup>
                        <InputGroupInput
                          id="company"
                          {...register('company')}
                          placeholder={resume.form.companyPlaceholder}
                          aria-invalid={!!errors.company}
                        />
                        <InputGroupAddon className="text-foreground">
                          <Building2 className="stroke-2" />
                        </InputGroupAddon>
                      </InputGroup>
                      <FieldError>{errors.company?.message}</FieldError>
                    </FieldContent>
                  </Field>

                  <Field data-invalid={!!errors.position}>
                    <FieldLabel htmlFor="position">
                      {resume.form.positionLabel}
                    </FieldLabel>
                    <FieldContent>
                      <InputGroup>
                        <InputGroupInput
                          id="position"
                          {...register('position')}
                          placeholder={resume.form.positionPlaceholder}
                          aria-invalid={!!errors.position}
                        />
                        <InputGroupAddon className="text-foreground">
                          <Briefcase className="stroke-2" />
                        </InputGroupAddon>
                      </InputGroup>
                      <FieldError>{errors.position?.message}</FieldError>
                    </FieldContent>
                  </Field>
                </div>
              </FieldGroup>
            </FieldSet>

            <FieldSeparator />

            <FieldSet>
              <FieldLegend>{resume.form.messageLegend}</FieldLegend>
              <FieldDescription>{resume.form.messageLabel}</FieldDescription>
              <Field data-invalid={!!errors.message}>
                <FieldContent>
                  <InputGroup>
                    <InputGroupTextarea
                      id="message"
                      {...register('message')}
                      placeholder={resume.form.messagePlaceholder}
                      className="w-full min-w-0 wrap-break-word"
                      style={{
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere',
                      }}
                      aria-invalid={!!errors.message}
                    />
                    <InputGroupAddon align="block-end">
                      <InputGroupText
                        className={cn(
                          'text-xs font-normal',
                          errors.message
                            ? 'text-destructive'
                            : 'text-muted-foreground',
                        )}
                      >
                        {message?.length ?? 0}/{MAX_MESSAGE_LENGTH} characters
                      </InputGroupText>
                    </InputGroupAddon>
                  </InputGroup>
                  <FieldError>{errors.message?.message}</FieldError>
                </FieldContent>
              </Field>
            </FieldSet>

            <Field orientation="horizontal" className="justify-end">
              <DialogClose variant="outline" isDisabled={isSubmitting}>
                {resume.form.cancel}
              </DialogClose>
              <Button type="submit" isDisabled={isSubmitting}>
                {isSubmitting ? <Spinner /> : <Send />}
                {resume.form.submit}
              </Button>
            </Field>
          </FieldGroup>
        </form>
      </Dialog>
    </DialogTrigger>
  );
};
